import { useMemo, useState, useLayoutEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { projects } from "@/data/projects";
import { ProjectCard } from "@/components/work/ProjectCard";
import { ServicesHeader } from "@/components/ServicesHeader";
import { useSiteLanguage } from "@/lib/useSiteLanguage";
import { workPageCopy } from "@/lib/siteCopy";
import {
  getProjectCategoryKeys,
  localizeCategoryLabel,
  localizeProjects,
} from "@/lib/localizeProjects";

type NavItem = {
  label: string;
  path?: string;
  href?: string;
  id?: string;
  type: "route" | "scroll" | "external";
};

interface WorkPageProps {
  navItems: NavItem[];
  onMobileMenuOpen?: () => void;
}

export default function WorkPage({
  navItems,
  onMobileMenuOpen,
}: WorkPageProps) {
  const lang = useSiteLanguage();
  const t = workPageCopy[lang];
  const categoryKeys = useMemo(
    () => ["All", ...getProjectCategoryKeys()],
    [],
  );
  const [activeCategory, setActiveCategory] = useState("All");

  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const localized = useMemo(
    () => localizeProjects(projects, lang),
    [lang],
  );

  const filtered = useMemo(() => {
    if (activeCategory === "All") return localized;
    // Filter by English category key against original data ids via matching localized list
    // by pairing: keep projects whose English category matches activeCategory
    return projects
      .filter((p) => p.category === activeCategory)
      .map((p) => localizeProjects([p], lang)[0]!);
  }, [activeCategory, lang, localized]);

  return (
    <div className="min-h-screen bg-background">
      <ServicesHeader
        navItems={navItems}
        onMobileMenuOpen={onMobileMenuOpen}
        variant="work"
      />

      <div className="px-4 pb-24 pt-28 md:px-6">
        <div className="mx-auto max-w-6xl">
          <div className="mb-12 flex flex-col gap-8 md:flex-row md:items-end md:justify-between">
            <div>
              <span className="font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground">
                {String(projects.length).padStart(2, "0")} {t.count}
              </span>
              <h1 className="mt-3 text-4xl font-bold tracking-tight md:text-5xl">
                {t.title}
              </h1>
              <p className="mt-3 max-w-md text-muted-foreground">{t.subtitle}</p>
            </div>

            <div className="flex flex-wrap gap-2">
              {categoryKeys.map((category) => {
                const isActive = category === activeCategory;
                const label =
                  category === "All"
                    ? t.all
                    : localizeCategoryLabel(category, lang);
                return (
                  <button
                    key={category}
                    onClick={() => setActiveCategory(category)}
                    className={`relative rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                      isActive
                        ? "text-background"
                        : "text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    {isActive && (
                      <motion.span
                        layoutId="active-category-pill"
                        className="absolute inset-0 rounded-full bg-foreground"
                        transition={{
                          type: "spring",
                          stiffness: 350,
                          damping: 30,
                        }}
                      />
                    )}
                    <span className="relative z-10">{label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <motion.div layout className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <AnimatePresence mode="popLayout">
              {filtered.map((project, index) => (
                <ProjectCard
                  key={project.id}
                  project={project}
                  index={index}
                  featured={Boolean(project.featured) && activeCategory === "All"}
                />
              ))}
            </AnimatePresence>
          </motion.div>

          {filtered.length === 0 && (
            <div className="py-24 text-center text-muted-foreground">
              {t.empty}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
